import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feather, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { STORAGE_KEYS, clearOnboardingStorage } from '@/constants/onboardingConfig';

export default function WelcomePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Load preview from localStorage before any cleanup
    const raw = localStorage.getItem(STORAGE_KEYS.PREVIEW);
    if (raw) {
      try {
        setPreview(JSON.parse(raw));
      } catch {}
    }

    // Attach the onboarding session to the user account
    const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    if (sessionId) {
      axios
        .post(`${API}/onboarding/attach`, { session_id: sessionId })
        .catch(() => {});
    }

    // Fetch user info
    axios
      .get(`${API}/auth/me`)
      .then((res) => setUser(res.data))
      .catch(() => navigate('/dashboard', { replace: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBegin = async () => {
    setCreating(true);
    try {
      const title = preview?.title || 'My Story';
      const res = await axios.post(`${API}/books`, {
        title,
        genre: 'memoir',
        description: preview?.reflection || '',
        is_interview_mode: false,
      });
      const bookId = res.data.book_id;

      // Seed Chapter 1 with the preview prose the user already saw
      if (preview?.preview) {
        await axios.post(`${API}/books/${bookId}/chapters`, {
          title,
          chapter_number: 1,
          content: preview.preview,
        }).catch(() => {});
      }

      // All onboarding storage cleared here — only after successful consumption
      clearOnboardingStorage();
      navigate(`/books/${bookId}`, { replace: true });
    } catch {
      setCreating(false);
    }
  };

  const handleDashboard = () => {
    clearOnboardingStorage();
    navigate('/dashboard', { replace: true });
  };

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#1A1816] flex flex-col items-center justify-center px-6 fade-in">
      <div className="max-w-sm w-full text-center">
        <Feather className="w-8 h-8 text-[#5C4D6B] dark:text-[#B0A0C0] mx-auto mb-6" />

        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2">
          {firstName ? `Welcome, ${firstName}.` : 'Your story is here.'}
        </h1>
        <p className="text-[#6B6560] dark:text-[#9C9590] text-sm mb-8">
          Your memory has been saved. Here's how it begins.
        </p>

        {/* Preview card */}
        {preview && (
          <div className="bg-white/60 dark:bg-[#22201C]/70 border border-[#E0D8CC]/60 dark:border-[#2A2520]/60 rounded-2xl p-6 mb-8 backdrop-blur-sm text-left">
            {preview.reflection && (
              <p className="font-serif text-sm italic text-[#6B6560] dark:text-[#9C9590] leading-relaxed mb-4">
                {preview.reflection}
              </p>
            )}
            {preview.title && (
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-[#E0D8CC] dark:bg-[#2A2520]" />
                <span className="font-serif text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">
                  {preview.title}
                </span>
                <div className="flex-1 h-px bg-[#E0D8CC] dark:bg-[#2A2520]" />
              </div>
            )}
            {preview.preview && (
              <p className="font-serif text-base italic text-[#2A2420] dark:text-[#F0EBE4] leading-relaxed">
                {preview.preview}
              </p>
            )}
          </div>
        )}

        <Button
          onClick={handleBegin}
          disabled={creating}
          className="w-full bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6A5980] text-white rounded-full py-6 font-medium mb-3 shadow-[0_8px_30px_-8px_rgba(92,77,107,0.35)] transition-all duration-300"
        >
          {creating ? 'Creating your book…' : (
            <>
              Continue this story
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>

        <button
          onClick={handleDashboard}
          className="text-sm text-[#A09890] dark:text-[#5C5650] hover:text-[#6B6560] dark:hover:text-[#9C9590] transition-colors"
        >
          Go to dashboard instead
        </button>
      </div>
    </div>
  );
}
